import React, { useState } from 'react';
import Modal from 'components/commons/modal/Modal2';
import Icon from 'components/commons/icon/Icon';
import { GetUserBrokerResultItem, prepareUserBroker } from 'config/api';
import { convertToMonoBrokerId } from 'components/features/utils/converter';
import { useRouting } from 'hooks/useRouting';
import Loading from 'components/commons/loading/Loading';
import GoMoneytreeSiteDialog from 'components/features/MoveMoneytreeSiteDialog';
import * as consts from 'config/consts';
import * as env from 'config/environment';

export const SecurityAccountCooperationErrorModal = ({
  hideModal,
  isShowModal,
  brokerItem,
  doAfterSyncAccountAsset,
}: {
  isShowModal: boolean;
  hideModal: () => void;
  brokerItem: GetUserBrokerResultItem;
  doAfterSyncAccountAsset?: () => void;
}): React.ReactElement => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isShowUpdate, setShowUpdate] = useState<boolean>(false);
  const [showGoMoneytreeSiteDialog, setShowGoMoneytreeSiteDialog] = useState<
    boolean
  >();
  const routing = useRouting();

  const doGoMoneytreeSiteOK = (): void => {
    window.open(
      `${env.moneytreeManagementURL}/connection/${brokerItem?.Account}?client_id=${env.moneytreeClientId}`,
      '_blank'
    );
    setShowGoMoneytreeSiteDialog(false);
  };

  // テコテックの編集は、MONOの新規登録に遷移させる
  // ただし、並行稼働完了後に破棄
  const doChangeCritter = async (
    brokerItem: GetUserBrokerResultItem
  ): Promise<void> => {
    setLoading(true);
    const brokerID = convertToMonoBrokerId(brokerItem.BrokerID);

    const res = await prepareUserBroker({ brokerID });
    if (res.Success) {
      window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${brokerID}?onetimeToken=${res.OnetimeToken}&provisionalId=${res.ProvisionalID}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${env.appURL}/securityaccounts&completeUrl=${env.appURL}/securityaccounts`;
    } else if (!res.Success && res.Status === '410') {
    }
  };
  const doChangeMono = async (
    brokerItem: GetUserBrokerResultItem
  ): Promise<void> => {
    setLoading(true);

    const res = await prepareUserBroker({
      brokerID: brokerItem.BrokerID,
      account: brokerItem.Account,
    });
    if (res.Success) {
      window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${brokerItem.BrokerID}?onetimeToken=${res.OnetimeToken}&monoAccountAggregationId=${brokerItem.Account}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${env.appURL}/securityaccounts&completeUrl=${env.appURL}/securityaccounts`;
    } else if (!res.Success && res.Status === '410') {
    }
  };

  const doChange = (): void => {
    if (!brokerItem) return;

    switch (brokerItem.AggregateSource) {
      case consts.AggregateSourceTypes.テコテック:
        doChangeCritter(brokerItem);
        break;

      case consts.AggregateSourceTypes.MONO:
        doChangeMono(brokerItem);
        // TODO: MONOの場合の口座詳細画面の編集ボタンを押した際の挙動
        break;
      case consts.AggregateSourceTypes.マネーツリー:
        setShowGoMoneytreeSiteDialog(true);
        break;

      case consts.AggregateSourceTypes.手入力:
        routing.pushRouting(
          { path: `/optionalassetrenewal/${brokerItem.Account}` },
          true
        );
        break;
    }
  };

  const doHideModal = (): void => {
    setShowUpdate(false);
    hideModal();
  };

  const onAfterSyncAccuntAsset = (): void => {
    setShowUpdate(false);
    doAfterSyncAccountAsset && doAfterSyncAccountAsset();
  };

  const onShowUpdate = (): void => {
    setShowUpdate(!isShowUpdate);
  };

  const syncAccountAssetBlock = (isOpen?: boolean) => {
    return (
      <>
        {isOpen ? (
          <>
            <div
              className="flex flex-row items-center mt-4 text-blue-700 cursor-pointer"
              onClick={onAfterSyncAccuntAsset}>
              <div className="mr-2">口座情報を更新</div>
              <div className="text-sm">
                <div className="icon-reflesh"></div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="flex items-center mt-2 cursor-pointer"
              onClick={onShowUpdate}>
              <div>
                <i
                  className={`${
                    isShowUpdate ? 'icon-arrow_drop_up' : 'icon-arrow_drop_down'
                  } text-4xl text-gray-700 pb-2`}></i>
              </div>
              <div className="text-bluegray-500">
                証券会社でお手続きを済ませた方はこちら
              </div>
            </div>
            {isShowUpdate && (
              <>
                <div className="mt-2">
                  下記の「更新」を押すと最新の情報を取得します。
                </div>
                <div
                  className="flex flex-row items-center mt-4 text-blue-700 cursor-pointer"
                  onClick={onAfterSyncAccuntAsset}>
                  <div className="mr-2">口座情報を更新</div>
                  <div className="text-sm">
                    <div className="icon-reflesh"></div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  };

  const title = (status: string | null) => {
    if (status === '001') {
      return <>次回の更新までしばらくお待ちください</>;
    } else if (status === '009') {
      return <>次回の更新までしばらくお待ちください</>;
    } else if (status === '002') {
      return <>証券会社がメンテナンス中のため、次回更新までお待ちください</>;
    } else if (status === '004') {
      return <>証券会社の公式サイトで「アカウントロック」を解除してください</>;
    } else if (status === '005') {
      return (
        <>証券会社の公式サイトで「ログインパスワード」を変更してください</>
      );
    } else if (status === '006') {
      return <>証券会社の公式サイトで「多要素認証」を解除してください</>;
    } else if (status === '007') {
      return (
        <>
          証券会社の公式サイトでメッセージを確認し、ログインを承認してください
        </>
      );
    } else if (status === '008') {
      return <>カスタマーサポートへお問合わせください</>;
    } else if (status === '900' || status === '901') {
      return <>更新を停止しています</>;
    } else if (status === '999') {
      return <>次回の更新までしばらくお待ちください</>;
    } else {
      return <>カスタマーサポートへお問合わせください</>;
    }
  };

  const message = (status: string | null) => {
    if (status === '001') {
      return (
        <>
          <div>
            情報が取得できませんでした。次回の更新までしばらくお待ちください。
          </div>
          <div className="mt-4">
            情報更新タイミングについて
            <br />
            月曜〜金曜の朝8時ごろと夕方18時ごろ、土曜の朝8時ごろに証券会社から取得した情報を更新します。
          </div>
          <div>(エラーコード：{status})</div>
          <div className="mt-4">
            ※持株会、IPO銘柄の場合は、連携に2営業日ほどお時間をいただく場合があります。
          </div>
          {syncAccountAssetBlock(true)}
        </>
      );
    } else if (status === '009') {
      return (
        <>
          <div>
            情報が取得できませんでした。次回の更新までしばらくお待ちください。
          </div>
          <div className="mt-4">
            情報更新タイミングについて
            <br />
            月曜〜金曜の朝8時ごろと夕方18時ごろ、土曜の朝8時ごろに証券会社から取得した情報を更新します。
          </div>
          <div>(エラーコード：{status})</div>
          {syncAccountAssetBlock(true)}
        </>
      );
    } else if (status === '002') {
      return (
        <>
          <div>
            連携先の証券会社がメンテナンス中のため、最新の情報が取得できませんでした。次回の更新までしばらくお待ちください。
          </div>
          <div className="mt-4">
            情報更新タイミングについて
            <br />
            月曜〜金曜の朝8時ごろと夕方18時ごろ、土曜の朝8時ごろに証券会社から取得した情報を更新します。
          </div>
          <div>(エラーコード：{status})</div>
          {syncAccountAssetBlock(true)}
        </>
      );
    } else if (status === '004') {
      return (
        <>
          <div>
            情報が取得できませんでした。
            <br />
            証券会社のアカウントがロックされています。
            <br />
            証券会社の公式サイトでアカウントロックを解除してください。
          </div>
          <div>(エラーコード：{status})</div>
          {syncAccountAssetBlock()}
        </>
      );
    } else if (status === '005') {
      return (
        <>
          <div>
            最新の情報が取得できませんでした。
            <br />
            証券会社よりお客様のログインパスワードの変更を求められています。
            <br />
            証券会社の公式サイトにてご確認をお願いいたします。
          </div>
          <div>(エラーコード：{status})</div>
          {syncAccountAssetBlock()}
        </>
      );
    } else if (status === '006') {
      return (
        <>
          <div>
            証券会社のログインに「多要素認証」が設定されていると資産情報を取得することができません。
            <br />
            証券会社の公式ページで多要素認証の設定を解除してください。
          </div>
          <div>(エラーコード：{status})</div>
          <div className="mt-4 mb-2 text-blue-700">
            <a
              href="https://support.minkabu.jp/hc/ja/articles/17607041083289-%E5%A4%9A%E8%A6%81%E7%B4%A0%E8%AA%8D%E8%A8%BC%E3%81%A8%E3%81%AF"
              target="_blank"
              rel="noopener noreferrer">
              多要素認証とは
            </a>
          </div>
          {syncAccountAssetBlock()}
        </>
      );
    } else if (status === '007') {
      return (
        <>
          <div className="mb-2">
            情報が取得できませんでした
            <br />
            証券会社からお客様の確認が必要なメッセージがあるため、情報を取得することができませんでした。
            <br />
            証券会社の公式サイトにログインし、表示されているメッセージを確認してください。
            <div>(エラーコード：{status})</div>
          </div>
          {syncAccountAssetBlock()}
        </>
      );
    } else if (status === '008') {
      return (
        <>
          <div>
            情報が取得できませんでした
            <br />
            しばらくお待ちいただいても解消しない場合は、お手数をおかけしますがこちらからお問い合わせをお願いいたします。
          </div>
          <div>(エラーコード：{status})</div>
          <div className="mt-4 text-blue-700">
            <a
              href="https://support.minkabu.jp/hc/ja/requests/new?ticket_form_id=360002511233"
              target="_blank"
              rel="noopener noreferrer">
              お問い合せ
            </a>
          </div>
        </>
      );
    } else if (status === '900') {
      return (
        <>
          <div className="mb-2">
            セキュリティ強化の取り組みとして、３ヶ月間以上ログインがない状態が続くと口座情報の取得を一時停止いたします。
            <br />
            口座情報取得を再開するには、下記の更新ボタンより更新を行なってください
            <div>(エラーコード：{status})</div>
          </div>
          {syncAccountAssetBlock(true)}
        </>
      );
    } else if (status === '901') {
      return (
        <>
          <div>
            こちらの口座の情報更新には「みんかぶプレミアム（初回30日間無料）」の登録が必要です。
          </div>
          <div>(エラーコード：{status})</div>
          <div className="mt-4 text-blue-700">
            <a
              href={env.helpPremiumURL}
              target="_blank"
              rel="noopener noreferrer">
              みんかぶプレミアムとは
            </a>
          </div>
        </>
      );
    } else if (status === '999') {
      return (
        <>
          <div>
            情報が取得できませんでした。
            <br />
            次回の更新までしばらくお待ちください。
            <br />
            情報更新タイミングについて：毎営業日の8時と18時ごろに証券会社から取得した情報を更新します。
            <br />
            翌営業日以降も解消しない場合は、お手数をおかけしますがこちらからお問合せをお願いいたします。
          </div>
          <div>(エラーコード：{status})</div>
          <div className="my-4 text-blue-700">
            <a
              href="https://support.minkabu.jp/hc/ja/requests/new?ticket_form_id=360002511233"
              target="_blank"
              rel="noopener noreferrer">
              お問い合せ
            </a>
          </div>
          {syncAccountAssetBlock(true)}
        </>
      );
    } else {
      return (
        <>
          <div>アクセスの有効期限が切れたため口座の再連携をお願いします。</div>
          <div className="mt-4 text-blue-700 cursor-pointer" onClick={doChange}>
            再連携する
          </div>
        </>
      );
    }
  };

  return (
    <Modal isShow={isShowModal} close={doHideModal} closeOnClickOutsideWindow>
      <div className="p-4">
        <div className="flex flex-row">
          <Icon name="alert" className="flex items-center h-5 text-red pr-2" />
          <div className="text-base font-bold">{title(brokerItem.Status)}</div>
          <div
            className="icon-close ml-auto text-gray-500 text-2xl cursor-pointer"
            onClick={doHideModal}
          />
        </div>
        <div className="mt-4 text-sm">{message(brokerItem.Status)}</div>
        <div
          onClick={doHideModal}
          className="flex justify-center text-gray-500 mt-4 mb-1 mx-1 p-2 text-base cursor-pointer">
          閉じる
        </div>
      </div>
      {showGoMoneytreeSiteDialog && (
        <GoMoneytreeSiteDialog
          onOK={doGoMoneytreeSiteOK}
          onCancel={(): void => setShowGoMoneytreeSiteDialog(false)}
        />
      )}
      {isLoading && (
        <div className="absolute top-0 left-0 h-full w-full flex justify-center items-center z-20">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              backgroundColor: 'transparent',
            }}
          />
        </div>
      )}
    </Modal>
  );
};
